// ─── Regime-aware tax-vocabulary resolver (presentational) ───────────────────
//
// The single frontend source of truth for "what tax words does this tenant
// see?" — GST vs VAT, GSTIN vs TRN, split vs single line, and which
// India-only statutory surfaces (GSTR1 / Tally / HSN) are visible.
//
// This is a framework-neutral mirror of the backend
// `services/api/src/billing/tax-presentation.ts` contract. The backend module
// is the authoritative contract; this file does NOT redefine the mapping — it
// derives the regime through the same shared `getTaxRegime()` in `@bbpos/types`
// that the backend uses, so the two stay in lockstep with no divergent
// thresholds. Labels/visibility only — calculation engines (rounding) are
// untouched; `lineRounding` is exposed for display parity, not arithmetic.
//
// `apps/admin` / `apps/pos` wrap this in a `useTaxRegime()` hook (tenant
// currency → presentation); shared `@bbpos/ui` primitives import the pure
// `resolveTaxRegime()` directly. INR resolves to `GST_INDIA` and every label
// is byte-identical to the legacy hardcoded strings.

import {
  SUPPORTED_CURRENCIES,
  getTaxRegime,
  getTaxIdFormat,
  getCurrencyForCountry,
  supplierTaxIdFormat,
} from '@bbpos/types';
import type { CurrencyCode, TaxRegime } from '@bbpos/types';

/**
 * The complete regime-aware tax vocabulary for one tenant. Pure data — safe to
 * memoize, serialize, and consume from any framework (or none).
 */
export interface TaxRegimePresentation {
  /** The resolved statutory regime (mirrors backend `getTaxRegime`). */
  readonly regime: TaxRegime;
  /** Short tax name for headers/labels: `GST` (India) · `VAT` (GCC) · `Tax` (no-VAT). */
  readonly taxName: 'GST' | 'VAT' | 'Tax';
  /**
   * Statutory tax-identifier label, mirroring backend `taxIdLabel()` exactly:
   * India `GSTIN`; UAE/KSA `TRN`; Oman `VATIN`; Bahrain `VAT No.`;
   * Kuwait/Qatar `null` (no statutory id ⇒ render nothing).
   */
  readonly taxIdLabel: string | null;
  /**
   * Validation pattern for the tax id, from `@bbpos/types` `REGIME_TAX_ID_FORMAT`
   * (India GSTIN; UAE/Bahrain 15 digits; KSA 15 digits starting/ending with 3;
   * Oman `OM` + 10 digits); `null` ⇒ no id.
   */
  readonly taxIdRegex: RegExp | null;
  /** Human hint shown beside the tax-id field; `null` when there is no id. */
  readonly taxIdHint: string | null;
  /** Exact tax-id length (input `maxLength`); `null` when there is no id. */
  readonly taxIdLength: number | null;
  /** Example value for placeholders; `null` when there is no id. */
  readonly taxIdExample: string | null;
  /**
   * Per-rate split column labels. India splits into `CGST/SGST/IGST`; every
   * GCC regime is a single VAT line ⇒ `null` (caller renders one `taxName`
   * column). Mirrors backend `buildTaxTotalLines` / `taxColumnHeader`.
   */
  readonly splitLabels: readonly ['CGST', 'SGST', 'IGST'] | null;
  /** India-only GSTR1 export surface — hidden for every GCC regime. */
  readonly showGstr1: boolean;
  /** India-only Tally export surface — hidden for every GCC regime. */
  readonly showTally: boolean;
  /** India-only HSN/SAC code surface — hidden for every GCC regime. */
  readonly showHsn: boolean;
  /**
   * Per-line tax rounding mode, for display parity with backend `roundLineTax`:
   * India = `floor` (per `CLAUDE.md` GST rule), GCC = `half-up`. Informational
   * only — do NOT recompute tax in the frontend from this.
   */
  readonly lineRounding: 'floor' | 'half-up';
}

/**
 * Regime → presentation lookup. Frozen and total over `TaxRegime`, so it
 * cannot drift from the backend regime enum and the mapping is exhaustively
 * unit-testable. `taxIdLabel` reproduces backend `taxIdLabel()` per regime.
 * The tax-id pattern/hint/length/example are NOT listed here — they come from
 * the shared `REGIME_TAX_ID_FORMAT` in `@bbpos/types` (see `resolveTaxRegime`).
 */
type StaticPresentation = Omit<
  TaxRegimePresentation,
  'regime' | 'taxIdRegex' | 'taxIdHint' | 'taxIdLength' | 'taxIdExample'
>;

const REGIME_PRESENTATION: Readonly<Record<TaxRegime, StaticPresentation>> = Object.freeze({
  GST_INDIA: {
    taxName: 'GST',
    taxIdLabel: 'GSTIN',
    splitLabels: ['CGST', 'SGST', 'IGST'] as const,
    showGstr1: true,
    showTally: true,
    showHsn: true,
    lineRounding: 'floor',
  },
  VAT_UAE: {
    taxName: 'VAT',
    taxIdLabel: 'TRN',
    splitLabels: null,
    showGstr1: false,
    showTally: false,
    showHsn: false,
    lineRounding: 'half-up',
  },
  VAT_KSA: {
    taxName: 'VAT',
    taxIdLabel: 'TRN',
    splitLabels: null,
    showGstr1: false,
    showTally: false,
    showHsn: false,
    lineRounding: 'half-up',
  },
  VAT_OMAN: {
    taxName: 'VAT',
    taxIdLabel: 'VATIN',
    splitLabels: null,
    showGstr1: false,
    showTally: false,
    showHsn: false,
    lineRounding: 'half-up',
  },
  VAT_BAHRAIN: {
    taxName: 'VAT',
    taxIdLabel: 'VAT No.',
    splitLabels: null,
    showGstr1: false,
    showTally: false,
    showHsn: false,
    lineRounding: 'half-up',
  },
  NO_VAT: {
    taxName: 'Tax',
    taxIdLabel: null,
    splitLabels: null,
    showGstr1: false,
    showTally: false,
    showHsn: false,
    lineRounding: 'half-up',
  },
});

/**
 * Narrow an arbitrary tenant `default_currency` (typed `string` on the API
 * contract) to a supported {@link CurrencyCode}. Unknown / empty / legacy
 * values fall back to `'INR'` — the correct default for every existing tenant,
 * so the resolver stays byte-identical to today for any non-multi-currency
 * tenant. Same normalization as `useTenantCurrency#toCurrencyCode`.
 */
function normalizeCurrency(value: CurrencyCode | string | null | undefined): CurrencyCode {
  const code = (value ?? '').toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code)
    ? (code as CurrencyCode)
    : 'INR';
}

/**
 * Resolve the full tax vocabulary for a tenant currency. Pure — no I/O, total
 * over the supported currency set. Defensive: an absent / unknown / legacy
 * currency yields `INR → GST_INDIA`, identical to backend `regimeForCurrency`,
 * so every pre-multi-currency tenant is unchanged.
 */
export function resolveTaxRegime(
  currency: CurrencyCode | string | null | undefined,
): TaxRegimePresentation {
  const regime = getTaxRegime(normalizeCurrency(currency));
  const format = getTaxIdFormat(regime);
  return {
    regime,
    ...REGIME_PRESENTATION[regime],
    taxIdRegex: format?.pattern ?? null,
    taxIdHint: format?.hint ?? null,
    taxIdLength: format?.length ?? null,
    taxIdExample: format?.example ?? null,
  };
}

/** How a supplier's tax id is labelled, checked and hinted on a form. */
export interface SupplierTaxIdPresentation {
  label: string;
  regex: RegExp;
  placeholder: string;
  maxLength: number;
}

// The placeholders the supplier forms always showed for these regimes; the
// e2e suite finds the tax id field by them, so they stay.
const SUPPLIER_TAX_ID_PLACEHOLDER: Partial<Record<TaxRegime, string>> = {
  GST_INDIA: '27AAPFU0939F1ZV',
  VAT_UAE: '100123456700003',
};

/**
 * Tax-id presentation for a supplier registered in `iso` (client issue I-3):
 * the country's own label and rule for India and the GCC, `null` where the
 * country has no tax id (Kuwait, Qatar: no field), and a generic "Tax ID"
 * for any other country.
 */
export function resolveSupplierTaxId(iso: string): SupplierTaxIdPresentation | null {
  const format = supplierTaxIdFormat(iso);
  if (!format) return null;
  const currency = getCurrencyForCountry(iso);
  if (!currency) {
    return { label: 'Tax ID', regex: format.pattern, placeholder: 'Tax or VAT number', maxLength: format.length };
  }
  const regime = getTaxRegime(currency);
  return {
    label: REGIME_PRESENTATION[regime].taxIdLabel ?? 'Tax ID',
    regex: format.pattern,
    placeholder: SUPPLIER_TAX_ID_PLACEHOLDER[regime] ?? format.example,
    maxLength: format.length,
  };
}
